import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  FaBalanceScale,
  FaSearch,
  FaGraduationCap,
  FaRocket,
  FaFileAlt,
  FaBullseye,
  FaEye,
  FaLightbulb,
  FaArrowRight,
} from 'react-icons/fa';
import { ABOUT } from '../config/content';
import SEO from '../components/SEO';
import AboutHeroIllustration from '../components/AboutHeroIllustration';
import styles from './About.module.css';

/* ── Reusable reveal wrapper (matches Home page pattern) ── */
const Reveal: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

const TARGET_USER_ICONS = [<FaBalanceScale />, <FaSearch />, <FaGraduationCap />];
const PRODUCT_ICONS = [<FaRocket />, <FaFileAlt />];

const About: React.FC = () => {
  return (
    <main className={styles.page}>
      <SEO path="/about" />
      {/* Ambient glows */}
      <div className={`${styles.ambient} ${styles.ambient1}`} />
      <div className={`${styles.ambient} ${styles.ambient2}`} />

      {/* ── Hero ── */}
      <div className={styles.heroWrapper}>
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <motion.span
              className={styles.heroLabel}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              {ABOUT.hero.label}
            </motion.span>
            <motion.h1
              className={styles.heroTitle}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.7 }}
            >
              {ABOUT.hero.title}
            </motion.h1>
            <motion.p
              className={styles.heroSubtitle}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.7 }}
            >
              {ABOUT.hero.subtitle}
            </motion.p>
          </div>
          <motion.div
            className={styles.heroIllustration}
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.55, duration: 0.9, ease: 'easeOut' }}
          >
            <AboutHeroIllustration className={styles.heroIllustrationSvg} />
          </motion.div>
          <motion.div
            className={styles.heroMvSection}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.7 }}
          >
            {[
              { data: ABOUT.mission },
              { data: ABOUT.vision },
            ].map((item, i) => (
              <div className={styles.heroMvBlock} key={i}>
                <h3 className={styles.heroMvTitle}>{item.data.title}</h3>
                <p className={styles.heroMvText}>{item.data.text}</p>
              </div>
            ))}
          </motion.div>
        </div>
        <div className={styles.heroFade} />
      </div>

      <div className={styles.sectionDivider} />

      {/* ── Description ── */}
      <section className={styles.section}>
        <Reveal className={styles.descriptionBlock}>
          <div className={styles.sectionTag}>{ABOUT.description.label}</div>
          <h2 className={styles.sectionTitle}>{ABOUT.description.title}</h2>
          <p className={styles.descriptionText}>{ABOUT.description.text}</p>
        </Reveal>
      </section>

      <div className={styles.sectionDivider} />

      {/* ── Target Users ── */}
      <section className={styles.section}>
        <Reveal className={styles.container}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTag}>{ABOUT.targetUsers.label}</div>
            <h2 className={styles.sectionTitle}>{ABOUT.targetUsers.title}</h2>
            <p className={styles.sectionSubtitle}>{ABOUT.targetUsers.subtitle}</p>
          </div>
          <div className={styles.usersGrid}>
            {ABOUT.targetUsers.items.map((user, i) => (
              <div className={styles.userCard} key={i}>
                <div className={styles.userIcon}>{TARGET_USER_ICONS[i]}</div>
                <h3 className={styles.userTitle}>{user.title}</h3>
                <p className={styles.userDesc}>{user.description}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      <div className={styles.sectionDivider} />

      {/* ── Products ── */}
      <section className={styles.section}>
        <Reveal className={styles.container}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTag}>{ABOUT.products.label}</div>
            <h2 className={styles.sectionTitle}>{ABOUT.products.title}</h2>
            <p className={styles.sectionSubtitle}>{ABOUT.products.subtitle}</p>
          </div>
          <div className={styles.productsGrid}>
            {ABOUT.products.items.map((product, i) => (
              <a
                href={product.link}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.productCard}
                key={i}
              >
                <div className={styles.productIcon}>{PRODUCT_ICONS[i]}</div>
                <h3 className={styles.productTitle}>{product.title}</h3>
                <p className={styles.productDesc}>{product.description}</p>
                <span className={styles.productLink}>
                  Explore {product.title} <FaArrowRight />
                </span>
              </a>
            ))}
          </div>
        </Reveal>
      </section>

      <div className={styles.sectionDivider} />

      {/* ── Tagline ── */}
      <section className={styles.section}>
        <Reveal className={styles.container}>
          <div className={styles.taglineBlock}>
            <FaLightbulb className={styles.taglineIcon} />
            <h2 className={styles.taglineText}>{ABOUT.hero.subtitle}</h2>
          </div>
        </Reveal>
      </section>

      {/* ── CTA ── */}
      <div className={styles.ctaSection}>
        <Reveal className={styles.ctaContent}>
          <h2 className={styles.ctaTitle}>{ABOUT.cta.title}</h2>
          <p className={styles.ctaSubtitle}>{ABOUT.cta.subtitle}</p>
          <div className={styles.ctaButtons}>
            <a
              href={ABOUT.cta.ctaPrimaryLink}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.btnPrimary}
            >
              {ABOUT.cta.ctaPrimary} <FaArrowRight />
            </a>
            <Link to={ABOUT.cta.ctaSecondaryLink} className={styles.btnOutline}>
              {ABOUT.cta.ctaSecondary}
            </Link>
          </div>
        </Reveal>
      </div>
    </main>
  );
};

export default About;
